import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Grid,
  Paper,
  LinearProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  NavigateNext as NavigateNextIcon,
  Mail as MailIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { ShadowBox } from "../../../../components/styles/styledComponent";
import { getEmployeesPendingDocuments } from "../../../../api/employeeApi";
import { getLocations } from "../../../../api/locationApi";
import { getDepartments } from "../../../../api/departmentApi";
import CustomNoResultsOverlay from "./components/CustomNoResultsOverlay";

const filterInputStyle = {
  borderRadius: "10px",
  backgroundColor: "#fff",
  transition: "all 0.2s ease-in-out",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(226, 232, 240, 0.8)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(117, 34, 209, 0.4)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7522D1",
  },
};

const stringToColor = (string) => {
  if (!string) return "#7522D1";
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

// Premium Stats Card Subcomponent
const StatCard = ({ title, count, icon, color, gradient }) => {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "16px",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        borderLeft: `6px solid ${color}`,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 30px rgba(117, 34, 209, 0.08)",
          borderColor: "rgba(226, 232, 240, 0.8)",
          borderLeftColor: color,
          "& .stat-icon-wrapper": {
            transform: "scale(1.1)",
            backgroundColor: color,
            color: "#fff",
          }
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "-50%",
          right: "-30%",
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          background: gradient,
          opacity: 0.1,
          filter: "blur(20px)",
          pointerEvents: "none",
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.725rem",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em" }}>
            {count}
          </Typography>
        </Box>
        <Box
          className="stat-icon-wrapper"
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${color}15`,
            color: color,
            transition: "all 0.3s ease",
          }}
        >
          {icon}
        </Box>
      </Box>
    </Box>
  );
};

const PendingDocumentsDirectory = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [departmentFilter, setDepartmentFilter] = useState("ALL");

  // Filter lists
  const [locationsList, setLocationsList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);

  // Pagination states
  const [page, setPage] = useState(0); // 0-indexed for DataGrid
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [stats, setStats] = useState({
    totalCount: 0,
    totalPendingFiles: 0,
    avgProgress: 0
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Reminder Dialog State
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Load locations and departments once on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [locRes, depRes] = await Promise.all([
          getLocations(),
          getDepartments()
        ]);
        if (locRes && locRes.success) {
          setLocationsList(locRes.data || []);
        }
        if (depRes && depRes.success) {
          setDepartmentsList(depRes.data || []);
        }
      } catch (err) {
        console.error("Error loading filter dropdowns:", err);
      }
    };
    loadFilters();
  }, []);

  // Fetch pending documents directory data
  const fetchPendingData = async () => {
    try {
      setLoading(true);
      const filters = {
        page: page + 1, // API is 1-indexed
        limit: pageSize
      };
      if (locationFilter !== "ALL") filters.jobLocation = locationFilter;
      if (departmentFilter !== "ALL") filters.departmentId = departmentFilter;
      if (debouncedSearchQuery) filters.search = debouncedSearchQuery;

      const response = await getEmployeesPendingDocuments(filters);
      if (response && response.success) {
        if (response.data && Array.isArray(response.data.employees)) {
          setEmployees(response.data.employees);
          setTotalRows(response.data.totalItems || 0);
          if (response.data.stats) {
            setStats(response.data.stats);
          }
        } else {
          // Fallback if API returned flat array
          const list = response.data || [];
          setEmployees(list);
          setTotalRows(list.length);

          const totalCount = list.length;
          const totalPendingFiles = list.reduce((acc, emp) => acc + (emp.pendingDocsCount || 0), 0);
          const avgProgress = totalCount > 0 ? Math.round(list.reduce((acc, emp) => acc + (emp.progressPercentage || 0), 0) / totalCount) : 0;
          setStats({ totalCount, totalPendingFiles, avgProgress });
        }
      }
    } catch (error) {
      console.error("Error fetching pending documents directory:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch pending documents list",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [locationFilter, departmentFilter, debouncedSearchQuery]);

  // Fetch data when page, pageSize, filters, or search changes
  useEffect(() => {
    fetchPendingData();
  }, [page, pageSize, locationFilter, departmentFilter, debouncedSearchQuery]);

  const handleMenuOpen = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleViewReport = () => {
    if (selectedEmployee) {
      navigate(
        `/my-team/employee-directory/${selectedEmployee.id}/pending-documents`,
      );
      handleMenuClose();
    }
  };

  const openReminderDialog = () => {
    setReminderDialogOpen(true);
    handleMenuClose();
  };

  const handleSendReminder = async () => {
    setReminderLoading(true);
    // Simulate API delay for reminder dispatch
    setTimeout(() => {
      setReminderLoading(false);
      setReminderDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Pending documents checklist report successfully sent to ${selectedEmployee.firstName} and reporting manager ${selectedEmployee.manager}!`,
        severity: "success",
      });
    }, 1200);
  };

  const handleDownloadIndividualReport = () => {
    if (!selectedEmployee) return;
    const docNames = selectedEmployee.pendingDocsList
      ? selectedEmployee.pendingDocsList.split(", ")
      : [];

    const headers = ["Document Category", "Document Name", "Status"];
    const rows = docNames.map((name) => [
      "Pending Upload",
      name,
      "Pending"
    ]);

    const csvContent = [
      ["Pending Documents Audit Report"],
      [`Employee: ${selectedEmployee.firstName} ${selectedEmployee.lastName} (${selectedEmployee.employeeId || selectedEmployee.empNo || "N/A"})`],
      [`Department: ${selectedEmployee.department || "N/A"}`],
      [`Designation: ${selectedEmployee.designation || "N/A"}`],
      [`Milestone Progress: ${selectedEmployee.progressPercentage || 0}%`],
      [`Generated on: ${new Date().toLocaleDateString("en-GB")}`],
      [],
      headers,
      ...rows
    ]
      .map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Pending_Documents_${selectedEmployee.firstName}_${selectedEmployee.lastName}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const handleDownloadDirectoryReport = async () => {
    try {
      setLoading(true);
      const filters = {
        all: true
      };
      if (locationFilter !== "ALL") filters.jobLocation = locationFilter;
      if (departmentFilter !== "ALL") filters.departmentId = departmentFilter;
      if (debouncedSearchQuery) filters.search = debouncedSearchQuery;

      const response = await getEmployeesPendingDocuments(filters);
      if (response && response.success) {
        let list = [];
        if (response.data && Array.isArray(response.data.employees)) {
          list = response.data.employees;
        } else if (Array.isArray(response.data)) {
          list = response.data;
        }

        const headers = [
          "Employee No.",
          "Name",
          "Email",
          "Department",
          "Designation",
          "Pending Files Count",
          "Pending Documents",
          "Onboarding Progress"
        ];

        const rows = list.map((emp) => [
          emp.employeeId || emp.empNo || "N/A",
          `${emp.firstName} ${emp.lastName}`,
          emp.email || "N/A",
          emp.department || "N/A",
          emp.designation || "N/A",
          emp.pendingDocsCount || 0,
          emp.pendingDocsList || "N/A",
          `${emp.progressPercentage || 0}%`
        ]);

        const csvContent = [
          headers,
          ...rows
        ]
          .map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
          .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Pending_Documents_Audit_Summary_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      setSnackbar({
        open: true,
        message: "Failed to download directory report",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = employees;

  const columns = [
    {
      field: "employeeId",
      headerName: "EMPLOYEE NO.",
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#7522D1",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() =>
              navigate(`/my-team/employee-directory/${params.row.id}`)
            }
          >
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "EMPLOYEE DETAILS",
      width: 250,
      renderCell: (params) => {
        const fullName = `${params.row.firstName || ""} ${params.row.lastName || ""}`.trim();
        const avatarColor = stringToColor(fullName || params.row.email || "EE");
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%", minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: avatarColor,
                color: "#fff",
                width: 36,
                height: 36,
                fontSize: "0.95rem",
                fontWeight: 700,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {params.row.firstName?.[0] || ""}
              {params.row.lastName?.[0] || ""}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ fontWeight: 600, color: "#1e293b" }}
              >
                {fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {params.row.email || "No email"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "workLocation",
      headerName: "JOB LOCATION",
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            icon={
              <LocationIcon style={{ fontSize: "0.9rem", color: "#3b82f6" }} />
            }
            label={params.value || "Not Specified"}
            variant="outlined"
            sx={{
              borderColor: "#bfdbfe",
              color: "#1e3a8a",
              bgcolor: "#eff6ff",
              fontWeight: 500,
              borderRadius: "6px",
              fontSize: "0.8rem",
            }}
          />
        </Box>
      ),
    },
    {
      field: "department",
      headerName: "DEPARTMENT / ROLE",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: "#334155", fontWeight: 550 }}
          >
            {params.value || "N/A"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {params.row.designation || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "manager",
      headerName: "REPORTING MANAGER",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ color: "#475569" }}>
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "progressPercentage",
      headerName: "COMPLETION RATE",
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", width: "100%", pr: 2 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#475569" }}
            >
              {params.value}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={params.value}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                background: "linear-gradient(90deg, #8b5cf6 0%, #7522D1 100%)",
              },
            }}
          />
        </Box>
      ),
    },
    {
      field: "pendingDocsCount",
      headerName: "PENDING FILES",
      width: 130,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={`${params.value} Pending`}
            size="small"
            sx={{
              bgcolor: "#fff1f2",
              color: "#e11d48",
              border: "1px solid #ffe4e6",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          />
        </Box>
      ),
    },
    {
      field: "pendingDocsList",
      headerName: "PENDING DOCUMENT LIST",
      width: 320,
      renderCell: (params) => {
        const listStr = params.row.pendingDocsList || "";
        const docs = listStr ? listStr.split(", ") : [];
        if (docs.length === 0) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                None
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5, maxWidth: "100%", alignItems: "center", height: "100%" }}>
            {docs.slice(0, 2).map((doc, idx) => (
              <Chip
                key={idx}
                label={doc}
                size="small"
                sx={{
                  bgcolor: "#fff1f2",
                  color: "#e11d48",
                  border: "1px solid #ffe4e6",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  height: 22,
                  borderRadius: "6px",
                }}
              />
            ))}
            {docs.length > 2 && (
              <Tooltip title={docs.join(", ")} placement="top" arrow>
                <Chip
                  label={`+${docs.length - 2} more`}
                  size="small"
                  sx={{
                    bgcolor: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #e2e8f0",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    height: 22,
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, params.row)}
            sx={{ color: "#64748b" }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Breadcrumbs and Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs
          separator={
            <NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />
          }
          sx={{
            mb: 1.5,
            "& .MuiBreadcrumbs-li": {
              fontSize: "0.875rem",
            },
          }}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/dashboard")}
            sx={{
              cursor: "pointer",
              color: "#4F4937",
              fontWeight: 400,
              fontSize: "0.875rem",
              "&:hover": { color: "#7522D1" },
            }}
          >
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/my-team")}
            sx={{
              cursor: "pointer",
              color: "#4F4937",
              fontWeight: 400,
              fontSize: "0.875rem",
              "&:hover": { color: "#7522D1" },
            }}
          >
            My Team
          </Link>
          <Typography
            color="text.primary"
            sx={{
              color: "#7522D1",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            Pending Documents
          </Typography>
        </Breadcrumbs>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mt: 1.5,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#030007",
                letterSpacing: "-0.02em",
              }}
            >
              Pending Documents Audit Directory
            </Typography>
            <Typography variant="body2" sx={{ color: "#4F4937", mt: 0.5, fontSize: "0.95rem" }}>
              Centralized administrative panel to track, audit, and request
              required documents pending from employees.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadDirectoryReport}
              startIcon={<DownloadIcon />}
              sx={{
                borderColor: "#7522D1",
                color: "#7522D1",
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 600,
                bgcolor: "#ffffff",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: "#5e1ba8",
                  backgroundColor: "rgba(117, 34, 209, 0.04)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Download Report
            </Button>
            <Button
              variant="outlined"
              onClick={fetchPendingData}
              startIcon={<RefreshIcon />}
              sx={{
                borderColor: "#7522D1",
                color: "#7522D1",
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 600,
                bgcolor: "#ffffff",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: "#5e1ba8",
                  backgroundColor: "rgba(117, 34, 209, 0.04)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Sync Data
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Analytics KPI Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Employees Pending Uploads"
            count={stats.totalCount}
            icon={<WarningIcon sx={{ fontSize: 24 }} />}
            color="#EF4444"
            gradient="radial-gradient(circle, #EF4444 0%, rgba(239, 68, 68, 0) 70%)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Outstanding Files Total"
            count={stats.totalPendingFiles}
            icon={<AssignmentIcon sx={{ fontSize: 24 }} />}
            color="#F59E0B"
            gradient="radial-gradient(circle, #F59E0B 0%, rgba(245, 158, 11, 0) 70%)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Average Completion Rate"
            count={`${stats.avgProgress}%`}
            icon={<CheckCircleIcon sx={{ fontSize: 24 }} />}
            color="#7522D1"
            gradient="radial-gradient(circle, #7522D1 0%, rgba(117, 34, 209, 0) 70%)"
          />
        </Grid>
      </Grid>

      {/* Filter and Search Panel */}
      <Paper
        elevation={0}
        sx={{
          p: 0,
          overflow: "hidden",
          mb: 4,
          borderRadius: "16px",
          border: "1px solid rgba(117, 34, 209, 0.08)",
          boxShadow: "0 8px 32px rgba(148, 163, 184, 0.04)",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 2.5,
            alignItems: "center",
            borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
            bgcolor: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(12px)",
          }}
        >
          {/* Search bar */}
          <TextField
            size="small"
            placeholder="Search by Employee ID, Name or Email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 320 },
              "& .MuiOutlinedInput-root": filterInputStyle,
            }}
          />

          {/* Job Location Filter */}
          <FormControl size="small" sx={{ width: { xs: "100%", sm: 200 } }}>
            <InputLabel id="location-filter-label" sx={{ fontWeight: 500 }}>
              Job Location
            </InputLabel>
            <Select
              labelId="location-filter-label"
              id="location-filter"
              value={locationFilter}
              label="Job Location"
              onChange={(e) => setLocationFilter(e.target.value)}
              sx={filterInputStyle}
            >
              <MenuItem value="ALL">All Locations</MenuItem>
              {locationsList.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Department Filter */}
          <FormControl size="small" sx={{ width: { xs: "100%", sm: 200 } }}>
            <InputLabel id="department-filter-label" sx={{ fontWeight: 500 }}>
              Department
            </InputLabel>
            <Select
              labelId="department-filter-label"
              id="department-filter"
              value={departmentFilter}
              label="Department"
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={filterInputStyle}
            >
              <MenuItem value="ALL">All Departments</MenuItem>
              {departmentsList.map((dep) => (
                <MenuItem key={dep.id} value={dep.id}>
                  {dep.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* DataGrid container */}
        <Box sx={{ height: 600, width: "100%", bgcolor: "#ffffff" }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={totalRows}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50]}
            disableSelectionOnClick
            rowHeight={64}
            slots={{ noRowsOverlay: CustomNoResultsOverlay }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F8F5FC",
                borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#42374F",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(241, 245, 249, 0.8)",
              },
              "& .MuiDataGrid-row": {
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(117, 34, 209, 0.015)",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid rgba(226, 232, 240, 0.8)",
                backgroundColor: "#fff",
              },
              "& ::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "& ::-webkit-scrollbar-track": {
                background: "#f8fafc",
              },
              "& ::-webkit-scrollbar-thumb": {
                background: "#ddd",
                borderRadius: "4px",
              },
              "& ::-webkit-scrollbar-thumb:hover": {
                background: "#ccc",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Action Popover Menu */}
      {/* Action Popover Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(117, 34, 209, 0.08)",
            border: "1px solid rgba(117, 34, 209, 0.08)",
            p: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={handleViewReport}
          sx={{
            borderRadius: "8px",
            py: 1.2,
            px: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.04)",
              color: "#7522D1",
              "& .MuiListItemIcon-root": { color: "#7522D1" },
            },
          }}
        >
          <ListItemIcon sx={{ color: "#7522D1", minWidth: "36px" }}>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="View Audit Report"
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleDownloadIndividualReport}
          sx={{
            borderRadius: "8px",
            py: 1.2,
            px: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.04)",
              color: "#7522D1",
              "& .MuiListItemIcon-root": { color: "#7522D1" },
            },
          }}
        >
          <ListItemIcon sx={{ color: "#0ea5e9", minWidth: "36px" }}>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download CSV Report"
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </MenuItem>
        <MenuItem
          onClick={openReminderDialog}
          sx={{
            borderRadius: "8px",
            py: 1.2,
            px: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.04)",
              color: "#7522D1",
              "& .MuiListItemIcon-root": { color: "#7522D1" },
            },
          }}
        >
          <ListItemIcon sx={{ color: "#f97316", minWidth: "36px" }}>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Send Reminder"
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </MenuItem>
      </Menu>

      {/* Reminder Confirmation Dialog */}
      <Dialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxWidth: "480px",
            p: 1.5,
            boxShadow: "0 20px 40px rgba(117, 34, 209, 0.12)",
            border: "1px solid rgba(117, 34, 209, 0.08)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#030007", pb: 1, fontSize: "1.25rem" }}>
          Send Pending Documents Report
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "#4F4937", lineHeight: 1.6, fontSize: "0.9rem" }}
          >
            You are about to compile and dispatch the outstanding documents
            checklist report. This report details exactly which mandatory
            credentials are still pending verification or upload.
          </Typography>

          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "#f8fafc",
              borderRadius: "12px",
              border: "1px solid rgba(226, 232, 240, 0.8)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#030007", mb: 1.5 }}
            >
              Recipients of this Audit Report:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(117, 34, 209, 0.08)",
                      color: "#7522D1",
                      width: 32,
                      height: 32,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    EE
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#030007",
                        display: "block",
                      }}
                    >
                      Employee: {selectedEmployee?.firstName}{" "}
                      {selectedEmployee?.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {selectedEmployee?.email}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(245, 158, 11, 0.08)",
                      color: "#D97706",
                      width: 32,
                      height: 32,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    MG
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#030007",
                        display: "block",
                      }}
                    >
                      Reporting Manager: {selectedEmployee?.manager}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Direct Supervisor
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setReminderDialogOpen(false)}
            sx={{
              textTransform: "none",
              borderColor: "#7522D1",
              color: "#7522D1",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: "#5e1ba8",
                backgroundColor: "rgba(117, 34, 209, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={reminderLoading}
            onClick={handleSendReminder}
            sx={{
              textTransform: "none",
              bgcolor: "#7522D1",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
              boxShadow: "0 4px 12px rgba(117, 34, 209, 0.15)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                bgcolor: "#5e1ba8",
                boxShadow: "0 6px 16px rgba(117, 34, 209, 0.25)",
              },
            }}
          >
            {reminderLoading ? "Compiling & Sending..." : "Send Report Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
            "&.MuiAlert-filledSuccess": {
              backgroundColor: "#7522D1",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingDocumentsDirectory;
